import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { useInfiniteHandler } from 'hooks';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ISpotifyWatchlistImportBody,
  ISpotifyWatchlistSearchItem,
  ISpotifyWatchlistSearchParams,
  ISpotifyWatchlistSearchResponse,
} from './types';

const cleanSearchParams = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === '' || value === undefined || value === null) return false;
      return true;
    })
  );

export const useGetSpotifyWatchlistSearchInfinite = (
  params: ISpotifyWatchlistSearchParams,
  enabled = true
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<ISpotifyWatchlistSearchItem[], DefaultError>({
    queryKey: ['admin-spotify-watchlist-search', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'SpotifyWatchlist/Search',
        params: cleanSearchParams({
          ...params,
          itemCount,
          page: pageParam,
        }),
      });

      const value = await responseHandler<ISpotifyWatchlistSearchResponse>(
        response,
        {
          groupName: `useGetSpotifyWatchlistSearchInfinite - ${pageParam}`,
        }
      );

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam,
    enabled,
  });
};

export const usePostSpotifyWatchlistImport = () => {
  const queryClient = useQueryClient();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostSpotifyWatchlistImport'
  );

  return useMutation<unknown, DefaultError, ISpotifyWatchlistImportBody>({
    async mutationFn({ file }) {
      loading(
        i18next.t('service-message.upload.loading', {
          what: i18next.t('spotifyWatchlist.what'),
        })
      );

      const formData = new FormData();
      formData.append('File', file);

      const response = await apiClient<FormData>({
        method: 'POST',
        url: 'SpotifyWatchlist/Import',
        body: formData,
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'usePostSpotifyWatchlistImport',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin-spotify-watchlist-search'],
      });
      success(
        i18next.t('service-message.upload.success', {
          what: i18next.t('spotifyWatchlist.what'),
        })
      );
    },
    onError: dismiss,
  });
};
